<template>
  <div class="sc-root">

    <!-- Cabeçalho da etapa -->
    <div class="sc-card">
      <div class="sc-card-header">
        <feather-icon icon="SettingsIcon" size="16" />
        <span>Configuração da Transmissão</span>
      </div>

      <div class="sc-card-body">
        <b-form @submit.prevent="saveConfig">

          <!-- ── Dados da transmissão ──────────────────────────── -->
          <p class="sc-section-title">
            <feather-icon icon="VideoIcon" size="13" class="mr-1" />
            Dados da Transmissão
          </p>

          <b-form-group label="Título">
            <b-form-input
              v-model="config.title"
              placeholder="Título da sua transmissão"
              required
            />
          </b-form-group>

          <b-form-group label="Descrição">
            <b-form-textarea
              v-model="config.description"
              placeholder="Descrição (opcional)"
              rows="3"
            />
          </b-form-group>

          <!-- ── Configurações técnicas ───────────────────────── -->
          <p class="sc-section-title mt-4">
            <feather-icon icon="SlidersIcon" size="13" class="mr-1" />
            Configurações Técnicas
          </p>

          <b-form-group label="Stream Key do YouTube">
            <b-form-input
              v-model="config.streamKey"
              placeholder="Cole sua Stream Key do YouTube Studio"
              required
            />
            <small class="text-muted">
              Encontre em: YouTube Studio → Configurações → Stream
            </small>
          </b-form-group>

          <!-- Vídeo já enviado -->
          <b-form-group label="Arquivo de Vídeo">
            <div v-if="config.videoUrl && !showChangeVideo" class="sc-video-uploaded">
              <div class="sc-video-info">
                <div class="sc-video-icon">
                  <feather-icon icon="PlayCircleIcon" size="20" class="text-success" />
                </div>
                <div>
                  <div class="sc-video-name">{{ getVideoFileName(config.videoKeyName) }}</div>
                  <span class="sc-badge sc-badge-success">
                    <feather-icon icon="CloudIcon" size="11" />
                    Disponível na nuvem
                  </span>
                </div>
              </div>
              <button type="button" class="sc-btn-outline" @click="showChangeVideo = true">
                Trocar
              </button>
            </div>

            <!-- Player de preview -->
            <div v-if="config.videoUrl && !showChangeVideo" class="sc-video-player mt-3">
              <video
                :src="config.videoUrl"
                controls
                preload="metadata"
                class="sc-video-el"
                @loadedmetadata="onVideoMetadataLoaded"
              />
              <div v-if="videoDuration || videoDimensions" class="sc-video-meta">
                <span v-if="videoDuration">{{ formatVideoDuration(videoDuration) }}</span>
                <span v-if="videoDuration && videoDimensions"> · </span>
                <span v-if="videoDimensions">{{ videoDimensions }}</span>
              </div>
            </div>

            <!-- Upload de vídeo -->
            <div v-if="!config.videoUrl || showChangeVideo">
              <b-form-file
                ref="videoFileInput"
                v-model="config.videoFile"
                accept="video/*"
                placeholder="Selecionar vídeo..."
                drop-placeholder="Solte o vídeo aqui"
                :required="!config.videoUrl"
                class="mb-2"
              />
              <div v-if="config.videoUrl && showChangeVideo" class="text-right">
                <button type="button" class="sc-btn-link" @click="cancelChangeVideo">
                  Cancelar
                </button>
              </div>
            </div>

            <!-- Barra de progresso de upload -->
            <div v-if="uploading" class="sc-upload-progress mt-3">
              <div class="sc-upload-header">
                <span class="text-muted">Enviando vídeo...</span>
                <span class="sc-upload-pct">{{ uploadPercentage }}%</span>
              </div>
              <div class="sc-progress-bar-wrap">
                <div class="sc-progress-bar" :style="{ width: uploadPercentage + '%' }"></div>
              </div>
            </div>
          </b-form-group>

          <!-- Botão salvar -->
          <button type="submit" class="sc-btn-primary mt-2" :disabled="uploading">
            <feather-icon v-if="!uploading" icon="SaveIcon" size="14" class="mr-1" />
            <span v-if="uploading">Enviando...</span>
            <span v-else>Salvar e Avançar</span>
          </button>
        </b-form>
      </div>
    </div>

  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormTextarea,
} from 'bootstrap-vue'
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'
import { useApiAWS } from '@/composables/useApi'
import axios from 'axios'

const apiAws = useApiAWS()

export default {
  name: 'StreamConfig',
  components: { BForm, BFormGroup, BFormInput, BFormFile, BFormTextarea },
  props: {
    isAuthenticated: { type: Boolean, default: false },
    webinarioData:   { type: Object,  default: null },
  },
  data() {
    return {
      config: {
        title:        '',
        description:  '',
        streamKey:    '',
        videoFile:    null,
        videoUrl:     '',
        videoKeyName: '',
        quality:      '1080p',
        bitrate:      3000,
        fps:          30,
      },
      uploading:           false,
      uploadPercentage:    0,
      uploadNotification:  null,
      showChangeVideo:     false,
      videoDuration:       0,
      videoDimensions:     '',
      VUE_APP_HOST_API_AWS_S3: process.env.VUE_APP_HOST_API_AWS_S3 || '/api/dkbucket',
      userData: JSON.parse(localStorage.getItem('userData')),
    }
  },
  watch: {
    uploadPercentage(v) {
      if (this.uploadNotification) {
        this.uploadNotification.message = `
          <strong>Upload ${v}%</strong>
          <div class="progress mt-1">
            <div class="progress-bar" style="width:${v}%">${v}%</div>
          </div>`
      }
    },
    webinarioData: {
      handler(w) { if (w) this.loadWebinarioToForm(w) },
      immediate: true,
    },
  },
  mounted() {
    this.loadConfig()
  },
  methods: {
    loadConfig() {
      const saved = localStorage.getItem('stream_config')
      if (!saved) return
      const parsed = JSON.parse(saved)
      this.config = { ...this.config, ...parsed, videoFile: null }
    },

    loadWebinarioToForm(w) {
      this.config = {
        ...this.config,
        title:        w.titulo          || '',
        description:  w.descricao       || '',
        streamKey:    w.stream_key      || '',
        videoUrl:     w.video_url       || '',
        videoKeyName: w.video_key_name  || '',
        quality:      w.qualidade       || '1080p',
        bitrate:      parseInt(w.bitrate, 10) || 3000,
        fps:          parseInt(w.fps, 10)     || 30,
      }
    },

    getVideoFileName(keyName) {
      if (!keyName) return 'Arquivo enviado'
      const parts = keyName.split('/')
      return parts[parts.length - 1] || 'Arquivo enviado'
    },

    cancelChangeVideo() {
      this.showChangeVideo = false
      this.config.videoFile = null
      if (this.$refs.videoFileInput) this.$refs.videoFileInput.reset()
    },

    async deletarVideoAntigoAWS(keyName) {
      if (!keyName) return
      try {
        await apiAws.delete('/remover-folder', { params: { folder_key_name: keyName } })
      } catch { /* background, sem notificação ao usuário */ }
    },

    normalizeFileName(name) {
      return name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .toLowerCase()
    },

    openUploadNotification() {
      this.uploadNotification = this.$notify({
        title: 'Upload do Vídeo',
        dangerouslyUseHTMLString: true,
        message: `<strong>Upload 0%</strong>
          <div class="progress mt-1">
            <div class="progress-bar" style="width:0%">0%</div>
          </div>`,
        position: 'bottom-right',
        duration: 0,
      })
    },

    closeUploadNotification() {
      if (this.uploadNotification) {
        this.uploadNotification.close()
        this.uploadNotification = null
      }
    },

    async uploadVideoToAWS(file) {
      if (!file) return null
      const oldKey = this.config.videoKeyName
      this.uploading = true
      this.uploadPercentage = 0

      const ext = file.name.substring(file.name.lastIndexOf('.'))
      const baseName = file.name.substring(0, file.name.lastIndexOf('.'))
      const normalizedFile = new File([file], this.normalizeFileName(baseName) + ext, { type: file.type })
      const caminhoPasta = `${this.userData.user_file_key}WebinarVideos/`

      const formData = new FormData()
      formData.append('video', normalizedFile)
      formData.append('caminho', caminhoPasta)
      formData.append('videoType', 'webinar')

      this.openUploadNotification()

      try {
        const response = await axios.post(
          `${this.VUE_APP_HOST_API_AWS_S3}/uploadVideos`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: e => {
              this.uploadPercentage = Math.round((e.loaded * 100) / e.total)
            },
          },
        )

        if (response.data.data.status === 201) {
          this.config.videoUrl     = response.data.data.fileUrl
          this.config.videoKeyName = response.data.data.key_filename

          if (oldKey && oldKey !== this.config.videoKeyName) {
            this.deletarVideoAntigoAWS(oldKey)
          }

          this.$toast.success('Vídeo enviado com sucesso!')
          return {
            url:               response.data.data.fileUrl,
            keyName:           response.data.data.key_filename,
            duration:          response.data.data.duration,
            durationFormatted: response.data.data.durationFormatted,
            fileName:          response.data.data.fileNameOrigin,
          }
        }
        throw new Error('Erro no upload do vídeo')
      } catch (error) {
        this.$toast.error(`Erro no upload: ${error.message}`)
        throw error
      } finally {
        this.uploading = false
        this.closeUploadNotification()
      }
    },

    async saveConfig() {
      if (!this.config.videoFile && !this.config.videoUrl) {
        this.$toast.error('Selecione um arquivo de vídeo!')
        return
      }

      try {
        if (this.config.videoFile && (!this.config.videoUrl || this.showChangeVideo)) {
          this.$toast.info('Enviando vídeo para o servidor...')
          const result = await this.uploadVideoToAWS(this.config.videoFile)
          if (!result) { this.$toast.error('Falha no upload do vídeo'); return }
          this.showChangeVideo = false
        }

        const userData = localStorage.getItem('userData')
        const userId = userData ? JSON.parse(userData).id : null

        const payload = {
          user_id:        userId,
          titulo:         this.config.title,
          descricao:      this.config.description,
          stream_key:     this.config.streamKey,
          qualidade:      this.config.quality,
          bitrate:        this.config.bitrate,
          fps:            this.config.fps,
          video_url:      this.config.videoUrl,
          video_key_name: this.config.videoKeyName,
        }

        await youtubeService.createWebinario(payload)

        const { videoFile, ...toSave } = this.config
        localStorage.setItem('stream_config', JSON.stringify(toSave))

        this.$emit('stream-ready', this.config)
        this.$emit('config-saved')
      } catch (error) {
        this.$toast.error(`Erro ao salvar: ${error.message || error}`)
      }
    },

    onVideoMetadataLoaded(e) {
      this.videoDuration  = e.target.duration
      this.videoDimensions = `${e.target.videoWidth}x${e.target.videoHeight}`
    },

    formatVideoDuration(s) {
      const h  = Math.floor(s / 3600)
      const m  = Math.floor((s % 3600) / 60)
      const ss = Math.floor(s % 60)
      if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
      return `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
    },
  },
}
</script>

<style lang="scss" scoped>
.sc-root { display: flex; flex-direction: column; gap: 16px; }

/* ── Card ─────────────────────────────────────────────────────── */
.sc-card {
  background: var(--dk-bg-card, #fff);
  border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow: hidden;
}
.sc-card-header {
  display: flex; align-items: center; gap: 8px;
  padding: 14px 20px;
  background: var(--dk-bg-page, #f8f9fb);
  border-bottom: 1.5px solid var(--dk-border-light, #f0f2f5);
  font-size: 0.88rem; font-weight: 700; color: var(--dk-text-primary, #0D3349);
}
.sc-card-body { padding: 20px 24px; }

/* ── Section title ────────────────────────────────────────────── */
.sc-section-title {
  font-size: 0.8rem; font-weight: 700; color: #E86A10;
  text-transform: uppercase; letter-spacing: 0.06em;
  display: flex; align-items: center; gap: 4px;
  margin-bottom: 14px;
}

/* ── Video uploaded card ──────────────────────────────────────── */
.sc-video-uploaded {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px;
  background: var(--dk-bg-page, #f5fdf8);
  border: 1.5px solid #b8f0d4;
  border-radius: 10px;
}
.sc-video-info    { display: flex; align-items: center; gap: 10px; }
.sc-video-icon    { flex-shrink: 0; }
.sc-video-name    { font-size: 0.82rem; font-weight: 600; color: var(--dk-text-primary, #344563); }
.sc-badge         { display: inline-flex; align-items: center; gap: 4px; font-size: 0.7rem; font-weight: 600; padding: 2px 8px; border-radius: 20px; }
.sc-badge-success { background: rgba(40,199,111,0.12); color: #28c76f; }

/* ── Video player ─────────────────────────────────────────────── */
.sc-video-player { border-radius: 10px; overflow: hidden; border: 1.5px solid var(--dk-border, #e9ecf2); }
.sc-video-el     { width: 100%; display: block; max-height: 260px; background: #000; }
.sc-video-meta   { padding: 7px 12px; font-size: 0.73rem; color: var(--dk-text-muted, #8e9aaa); }

/* ── Upload progress ──────────────────────────────────────────── */
.sc-upload-progress   { }
.sc-upload-header     { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 0.78rem; color: var(--dk-text-muted, #8e9aaa); }
.sc-upload-pct        { font-weight: 700; color: #E86A10; }
.sc-progress-bar-wrap { height: 6px; background: #e9ecf2; border-radius: 3px; overflow: hidden; }
.sc-progress-bar      { height: 100%; background: #E86A10; border-radius: 3px; transition: width 0.3s; }

/* ── Buttons ──────────────────────────────────────────────────── */
.sc-btn-primary {
  display: inline-flex; align-items: center; justify-content: center;
  width: 100%; padding: 10px 18px;
  border: none; border-radius: 8px;
  background: #E86A10; color: #fff;
  font-size: 0.84rem; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.sc-btn-primary:hover:not(:disabled) { background: #d05e0d; }
.sc-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

.sc-btn-outline {
  padding: 6px 14px;
  border: 1.5px solid #d0d7e3; border-radius: 7px;
  background: var(--dk-bg-card, #fff); color: var(--dk-text-secondary, #5a6370);
  font-size: 0.78rem; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s;
  white-space: nowrap;
}
.sc-btn-outline:hover { border-color: #0D3349; color: var(--dk-text-primary, #0D3349); }

.sc-btn-link {
  background: none; border: none;
  color: var(--dk-text-muted, #8e9aaa); font-size: 0.78rem;
  cursor: pointer; text-decoration: underline;
  padding: 0;
}
</style>
